import { BookingCurrency } from '~/types/booking'

export interface Subscription {
  price: SubscriptionPrice
  product: SubscriptionProduct
}
export interface SubscriptionPackage {
  productName: string
  productId: string
  className: string
  currency: BookingCurrency
  currencyOptions: any
  price: number
  priceId: string
  subscriptionPlanItems: any
  stripePaymentInterval: string
  priceApiIsLoading: boolean
  fullPrice: number
  licensePrice: number
  freeTrialPeriod: number
  durationInMonths: number
  isBestValue?: boolean
  isVippsSubscription?: boolean
  isFamilyPrice: boolean
}
export interface CurrentSubscription {
  productName: string
  priceId: string
  productId: string
  fullPrice: number
  currency: string
  paymentGateway: string
  coupon?: Coupon
  price: number
  isFamilySubscription: boolean
  interval: string
}

export interface SubscriptionPrice {
  id: number
  product: SubscriptionProduct
}

export interface SubscriptionProduct {
  id: number
  name: string
  readonly matadata: {
    [key: string]: string
  }
}

export interface StripeSubscription {
  id: string
  cancel_at: number
  cancel_at_period_end: boolean
  canceled_at: number
  currency: BookingCurrency
  current_period_end: number
  current_period_start: number
  customer: string
  discount: { coupon: Coupon; id: string }
  items: {
    data: StripeSubscriptionItemsData[]
  }
  start_date: number
  status: StripeSubscriptionStatus
  trial_start: number
  trial_end: number
  metadata: {
    paymentGateway: string
    vippsAgreementId?: string
    subscriptionType?: string // e.g., 'family'
  }
  upcomingInvoice: StripeUpcomingInvoice | null
}

// Minor units, both of them. `total` is the plan's recurring cost; `amount_due` also applies any
// account credit, so it is what Stripe actually takes and what the customer portal shows.
export interface StripeUpcomingInvoice {
  amount_due: number
  total: number
  currency: BookingCurrency
}

export interface StripeSubscriptionItemsData {
  id: string
  created: number
  readonly metadata: {
    [key: string]: string
  }
  plan: StripeSubscriptionPlan
  quantity: number
  price: StripePrice
  subscription: string
}

export interface StripeSubscriptionPlan {
  id: string // usually price id
  object: 'plan'
  active: boolean
  amount: number
  amount_decimal: string
  billing_scheme: string
  created: number
  currency: string
  interval: string
  interval_count: number
  livemode: boolean
  readonly metadata: {
    [key: string]: string
  }
  nickname: string | null
  product: string
  trial_period_days: number | null
}

export interface StripePrice {
  id: string
  object: 'price'
  active: boolean
  billing_scheme: string
  created: number
  currency: BookingCurrency
  currency_options: CurrencyOption
  custom_unit_amount: string | null
  nickname?: string | null
  readonly metadata: {
    [key: string]: string
  }
  product: StripeProduct
  recurring: {
    interval: string
    interval_count: number
    trial_period_days: number
  } | null
  unit_amount: number
  unit_amount_decimal: string
  coupon?: Coupon
  tiers?: Array<{
    flat_amount?: number
    flat_amount_decimal?: string
    unit_amount?: number
    unit_amount_decimal?: string
  }>
}

export interface CurrencyOption {
  [currencyCode: string]: {
    unit_amount: number
    unit_amount_decimal: string
    custom_unit_amount: number
  }
}

export interface Coupon {
  name: string
  id: string
  amount_off?: number
  percent_off?: number
  duration_in_months?: number
  duration?: 'once' | 'forever' | 'repeating'
  metadata?: {
    no_checkout_text?: string
    en_checkout_text?: string
    uk_checkout_text?: string
    hidePercentage?: string
  }
  code?: string
}

export interface StripeProduct {
  id: string
  object: 'product'
  active: boolean
  created: number
  default_price: string | null
  description: string | null
  readonly metadata: {
    [key: string]: string
  }
  name: string
  updated: number
}

export enum StripeSubscriptionStatus {
  INCOMPLETE = 'incomplete',
  INCOMPLETE_EXPIRED = 'incomplete_expired',
  TRIALING = 'trialing',
  ACTIVE = 'active',
  PAST_DUE = 'past_due',
  CANCELED = 'canceled',
  UNPAID = 'unpaid',
  PAUSED = 'paused',
}

export enum CurrentSubscriptionPlanStatus {
  START_NOW = 'startNow',
  CURRENT = 'currentPlan',
  UPGRADE = 'upgrade',
  CHANGE_PLAN = 'changePlan',
  BUY = 'buy',
  START_TRIAL = 'startTrial',
  SET_UP_FAMILY = 'setUpFamily',
  UPGRADE_INDIVIDUAL = 'upgradeIndividual',
  MANAGE_FAMILY_SUBSCRIPTION = 'manageFamilySubscription',
  GET_STARTED = 'getStarted',
}

export enum AdyenRedirectStatus {
  CANCELLED = 'cancelled',
  ERROR = 'error',
  REFUSED = 'refused',
  SUCCEEDED = 'succeeded',
  PENDING = 'pending',
  FAILED = 'failed',
}

export enum CouponApplicationStatus {
  APPLIED = 'applied',
  NOT_APPLIED = 'not_applied',
  DEFAULT = 'default',
}
